/**
 * 检索打分 —— 站内搜索与 `/api/agent/search` 共用同一份实现。
 *
 * 刻意不依赖任何内容模块：调用方传入 `{ title, haystack }`，这里只负责匹配与排序。
 * 两边各写一套打分的后果是「站内能搜到、Agent 搜不到」，那种差异很难解释也很难排查。
 */
export interface Rankable {
  title: string;
  /** 已转小写的整段可匹配文本（标题 + 描述 + 关键词）。 */
  haystack: string;
}

export interface Ranked<T> {
  item: T;
  score: number;
}

/** 按空格拆词；中文查询通常只有一个词，空格拆词对中英混输同样合适。 */
export function tokenizeQuery(raw: string): string[] {
  return raw.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** 全部词都要命中；标题命中权重高于其余字段，前缀命中再加权。返回 null 表示未命中。 */
export function scoreItem(item: Rankable, terms: readonly string[]): number | null {
  const title = item.title.toLowerCase();
  let score = 0;

  for (const term of terms) {
    if (!item.haystack.includes(term)) return null;
    score += title.includes(term) ? 6 : 3;
    if (title.startsWith(term)) score += 2;
  }

  return score;
}

/** 打分并按分数倒序，同分按标题排序，最后截断。 */
export function rankItems<T extends Rankable>(
  items: readonly T[],
  rawQuery: string,
  limit: number,
): Ranked<T>[] {
  const terms = tokenizeQuery(rawQuery);
  if (terms.length === 0) return [];

  const scored: Ranked<T>[] = [];
  for (const item of items) {
    const score = scoreItem(item, terms);
    if (score !== null) scored.push({ item, score });
  }

  return scored
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title, 'zh-Hans-CN'))
    .slice(0, limit);
}
